import React from 'react';
import PropTypes from 'prop-types';
import MDBox from 'components/MDBox';
import MDDatePicker from 'components/MDDatePicker';
import RichTextEditor from 'components/RichTextEditor';
import Swal from 'sweetalert2';
import useFormDraft from '../../hooks/useFormDraft';
import LoadDraftButton from '../../components/LoadDraftButton';
import { 
  Grid, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Checkbox, 
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  Box
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { api } from '../../api';
import { formatDateDDMMYYYY } from 'utils/dateUtils';

function MenstrualHistoryForm({ loading, onSubmit, previousData = [], onDelete, menstrualParams = null, contactId = null, patientGender = null }) {
  // Draft system for menstrual history
  const menstrualDraft = useFormDraft(`menstrual_history_${contactId}`, 1000);
  
  const [entries, setEntries] = React.useState([]);
  const [editingRecord, setEditingRecord] = React.useState(null);
  const [currentEntry, setCurrentEntry] = React.useState({
    lmp: '',
    menarche: '',
    cycleLength: '',
    duration: '',
    flow: '',
    pain: '',
    other: ''
  });

  // Initialize with any existing data
  React.useEffect(() => {
    if (previousData && previousData.length > 0) {
      setEntries(previousData);
    }
  }, [previousData]);

  // Function to fetch existing menstrual history from API
  const fetchExistingMenstrualHistory = async (contactId) => {
    try {
      if (!contactId || !menstrualParams?.param_group_members) {
        return [];
      }

      // Only fetch data for female patients
      if (patientGender && patientGender.toLowerCase() !== 'f') {
        console.log('Skipping menstrual history fetch - patient is not female:', patientGender);
        return [];
      }

      // Make API call to fetch contact history
      const response = await api.opd.getContactHistory(contactId);
      if (response.flag !== 1) {
        throw new Error('Failed to fetch contact history');
      }

      console.log('Fetched contact history:', response);
      const contactHistories = response?.data?.contact_histories || [];

      // Filter menstrual history records based on param group members
      const menstrualHistoryRecords = contactHistories.filter(history => {
        const slug = history.param_group_member?.slug;
        return slug && slug.startsWith('menstrual-history-params-');
      });

      // Group records by private_note (which contains the grouping timestamp)
      const groupedRecords = {};
      menstrualHistoryRecords.forEach(record => {
        const groupKey = record.private_note || 'default';
        if (!groupedRecords[groupKey]) {
          groupedRecords[groupKey] = {
            contact_history_ids: [],
            field_ids: {},
            field_param_ids: {},
            param_group_id: record.irm_param_groups_id,
            private_note: groupKey
          };
        }

        // Store all contact_history IDs for this group
        if (record.id) {
          groupedRecords[groupKey].contact_history_ids.push(record.id);
        }

        // Map the slug back to field name
        const slug = record.param_group_member?.slug;
        let fieldName = '';
        
        switch (slug) {
          case 'menstrual-history-params-lmp':
            fieldName = 'lmp';
            break;
          case 'menstrual-history-params-menarche':
            fieldName = 'menarche';
            break;
          case 'menstrual-history-params-cycle-length':
            fieldName = 'cycleLength';
            break;
          case 'menstrual-history-params-duration':
            fieldName = 'duration';
            break;
          case 'menstrual-history-params-flow':
            fieldName = 'flow';
            break;
          case 'menstrual-history-params-pain':
            fieldName = 'pain';
            break;
          case 'menstrual-history-params-others':
            fieldName = 'other';
            break;
          default:
            // Handle unknown slugs
            console.warn('Unknown menstrual history slug:', slug);
            fieldName = '';
            break;
        }

        if (fieldName) {
          // All fields are simple strings
          groupedRecords[groupKey][fieldName] = record.value && record.value !== 'NA' ? record.value : '';
          // Track which contact_history_id and param_group_member_id back each field, so a single field can be updated in place
          if (record.id) {
            groupedRecords[groupKey].field_ids[fieldName] = record.id;
          }
          if (record.irm_param_group_members_id) {
            groupedRecords[groupKey].field_param_ids[fieldName] = record.irm_param_group_members_id;
          }
        }
      });

      // Convert grouped records to array format
      const menstrualEntries = Object.values(groupedRecords).map(group => ({
        lmp: group.lmp || '',
        menarche: group.menarche || '',
        cycleLength: group.cycleLength || '',
        duration: group.duration || '',
        flow: group.flow || '',
        pain: group.pain || '',
        other: group.other || '',
        contact_history_ids: group.contact_history_ids,
        field_ids: group.field_ids,
        field_param_ids: group.field_param_ids,
        param_group_id: group.param_group_id,
        private_note: group.private_note,
        id: group.contact_history_ids[0] || Date.now()
      }));

      return menstrualEntries;
    } catch (error) {
      console.error('Error fetching menstrual history:', error);
      return [];
    }
  };

  // Fetch existing menstrual history when contactId or menstrualParams change
  React.useEffect(() => {
    const loadExistingData = async () => {
      if (contactId && menstrualParams?.param_group_members && patientGender) {
        console.log('Fetching menstrual history for contact ID:', contactId, 'Gender:', patientGender);
        
        // Only load data for female patients
        if (patientGender.toLowerCase() === 'f') {
          const existingEntries = await fetchExistingMenstrualHistory(contactId);
          console.log('Fetched menstrual entries:', existingEntries);
          setEntries(existingEntries);
        } else {
          console.log('Not loading menstrual history - patient is not female');
          setEntries([]);
        }
      }
    };

    loadExistingData();
  }, [contactId, menstrualParams, patientGender]);

  // Handler to load menstrual draft on button click
  const handleLoadMenstrualDraft = async () => {
    if (!contactId) return;
    const draft = await menstrualDraft.loadDraftData();
    if (draft && draft.currentEntry) {
      setCurrentEntry(draft.currentEntry);
    }
    if (draft && draft.entries) {
      setEntries(draft.entries);
    }
  };

  // Auto-save menstrual history draft
  React.useEffect(() => {
    if (!contactId) return;
    const hasCurrentData = Object.values(currentEntry).some(val => val && val.trim() !== '');
    const hasEntries = entries && entries.length > 0;
    
    if (hasCurrentData || hasEntries) {
      menstrualDraft.autoSave({ currentEntry, entries });
    }
  }, [currentEntry, entries, contactId]);

  // Get param options for form fields
  const getParamOptions = (fieldSlug) => {
    if (!menstrualParams?.param_group_members) return [];
    
    const paramMember = menstrualParams.param_group_members.find(
      member => member.slug === fieldSlug && member.is_active === 'Y'
    );
    
    return paramMember?.param_group_member_options || [];
  };

  // Get flow options from params
  const flowOptions = getParamOptions('menstrual-history-params-flow');
  const painOptions = getParamOptions('menstrual-history-params-pain');
  const cycleLengthOptions = getParamOptions('menstrual-history-params-cycle-length');

  const handleInputChange = (field, value) => {
    setCurrentEntry(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    
    // Validate that LMP is mandatory
    if (!currentEntry.lmp || currentEntry.lmp.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'LMP Required',
        text: 'LMP (Last Menstrual Period) date is mandatory. Please select a date.',
        confirmButtonColor: '#00798E'
      });
      return;
    }
    
    // Validate that at least one field is filled
    const hasData = Object.values(currentEntry).some(value => 
      Array.isArray(value) ? value.length > 0 : value.trim() !== ''
    );
    
    if (!hasData) {
      return;
    }

    // Create the entry for local state with temporary ID
    const newEntry = {
      ...currentEntry,
      id: Date.now() // Temporary ID for new entries
    };

    setEntries(prev => [...prev, newEntry]);
    
    // Prepare data for API submission
    if (onSubmit && menstrualParams?.param_group_members) {
      const currentDate = new Date().toISOString().split('T')[0]; // yyyy-mm-dd format
      let baseTimestamp = Math.floor(Date.now() / 1000); // Current unix timestamp
      let historyArray = [];

      // Field mappings to param member slugs
      const fieldMappings = {
        lmp: 'menstrual-history-params-lmp',
        menarche: 'menstrual-history-params-menarche',
        cycleLength: 'menstrual-history-params-cycle-length', 
        duration: 'menstrual-history-params-duration',
        flow: 'menstrual-history-params-flow',
        pain: 'menstrual-history-params-pain',
        other: 'menstrual-history-params-others'
      };
      // Process each field and create history entries
      Object.entries(fieldMappings).forEach(([fieldName, slug], index) => {
        console.log(`Processing field: ${fieldName}, slug: ${slug}, index: ${index}`);
        const fieldValue = currentEntry[fieldName];
        
        // Find the parameter member by slug — prefer an active one, but fall
        // back to an inactive match rather than silently dropping the field
        // (this is how LMP was disappearing when its param member wasn't active).
        const paramMember =
          menstrualParams.param_group_members.find(
            member => member.slug === slug && member.is_active === 'Y'
          ) ||
          menstrualParams.param_group_members.find(member => member.slug === slug);
        console.log(`paramMember for ${fieldName} (${slug}):`, paramMember);
        if (paramMember) {
          // Determine the value to use
          let valueToUse = 'NA';
          if (fieldValue) {
            if (Array.isArray(fieldValue) && fieldValue.length > 0) {
              valueToUse = fieldValue.join(', ');
            } else if (!Array.isArray(fieldValue) && fieldValue.trim() !== '') {
              valueToUse = fieldValue.toString();
            }
          }
          
          historyArray.push({
            contact_id: null, // Will be set by parent component
            param_group_id: paramMember.irm_param_groups_id,
            param_group_member_id: paramMember.id,
            param_group_member_option_id: null,
            name: paramMember.name,
            value: valueToUse,
            history_date: currentDate,
            remarks: null,
            private_note: (baseTimestamp).toString() // Increment by 2 for each entry
          });
        }
      });
      console.table("historyArray", historyArray);
      // Call parent onSubmit with the formatted data and clear draft on success
      const result = await menstrualDraft.handleSubmitWithDraft(
        async (data) => {
          await onSubmit(historyArray);
          return { success: true };
        },
        { currentEntry, entries }
      );
    }
    
    // Reset form
    setCurrentEntry({
      lmp: '',
      menarche: '',
      cycleLength: '',
      duration: '',
      flow: '',
      pain: '',
      other: ''
    });
  };

  const handleDeleteEntry = (index, entryId) => {
    setEntries(prev => prev.filter((_, i) => i !== index));
    
    // Call parent onDelete if provided
    if (onDelete && entryId) {
      onDelete(entryId);
    }
  };

  const handleDeleteMenstrualHistory = async (entry, index) => {
    if (!entry.contact_history_ids || entry.contact_history_ids.length === 0) {
      console.warn('No contact_history_ids found for this entry');
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Delete',
        text: 'No history IDs found for this entry',
        confirmButtonColor: '#00798E'
      });
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Delete Menstrual History?',
      text: 'Are you sure you want to delete this menstrual history record? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#999',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const deleteData = {
        contact_id: contactId,
        param_group_id: entry.param_group_id,
        contact_history_ids: entry.contact_history_ids
      };

      console.log('Deleting menstrual history:', deleteData);
      
      const response = await api.opd.deleteMenstrualHistory(deleteData);
      
      if (response.flag === 1) {
        console.log('Successfully deleted menstrual history');
        // Remove from local state
        setEntries(prev => prev.filter((_, i) => i !== index));
        
        // Show success message
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Menstrual history has been deleted successfully.',
          confirmButtonColor: '#00798E',
          timer: 2000,
          showConfirmButton: true
        });
      } else {
        console.error('Failed to delete menstrual history:', response);
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: response.message || 'Failed to delete menstrual history. Please try again.',
          confirmButtonColor: '#00798E'
        });
      }
    } catch (error) {
      console.error('Error deleting menstrual history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'An error occurred while deleting menstrual history.',
        confirmButtonColor: '#00798E'
      });
    }
  };

  const handleSaveEditedMenstrualHistory = async () => {
    if (!editingRecord) return;

    if (!editingRecord.lmp || editingRecord.lmp.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'LMP Required',
        text: 'LMP (Last Menstrual Period) date is mandatory. Please select a date.',
        confirmButtonColor: '#00798E'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Save Changes?',
      text: 'Do you want to update this menstrual history record?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00798E',
      cancelButtonColor: '#999',
      confirmButtonText: 'Yes, update it!'
    });

    if (!result.isConfirmed) return;

    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const fieldMappings = {
        lmp: 'menstrual-history-params-lmp',
        menarche: 'menstrual-history-params-menarche',
        cycleLength: 'menstrual-history-params-cycle-length',
        duration: 'menstrual-history-params-duration',
        flow: 'menstrual-history-params-flow',
        pain: 'menstrual-history-params-pain',
        other: 'menstrual-history-params-others'
      };

      // Find a param member for a slug — prefer an active one, but fall back to
      // an inactive match rather than silently dropping the field.
      const findParamMember = (slug) =>
        menstrualParams?.param_group_members?.find(m => m.slug === slug && m.is_active === 'Y') ||
        menstrualParams?.param_group_members?.find(m => m.slug === slug);

      const updates = [];
      const creates = [];

      Object.entries(fieldMappings).forEach(([fieldName, slug]) => {
        const contactHistoryId = editingRecord.field_ids?.[fieldName];
        const paramMember = findParamMember(slug);
        const paramMemberId = editingRecord.field_param_ids?.[fieldName] || paramMember?.id;

        if (!paramMemberId) return; // no such parameter defined — nothing we can save against

        const fieldValue = editingRecord[fieldName];
        const valueToUse = fieldValue && fieldValue.toString().trim() !== '' ? fieldValue.toString() : 'NA';

        if (contactHistoryId) {
          // Existing record for this field — update it in place
          updates.push({
            contact_history_id: contactHistoryId,
            value: valueToUse,
            history_date: currentDate,
            remarks: null,
            param_group_member_id: paramMemberId,
            param_group_member_option_id: null
          });
        } else {
          // No record exists yet for this field (e.g. it was never saved originally) —
          // create it instead of silently dropping the change (upsert).
          creates.push({
            contact_id: contactId,
            param_group_id: paramMember?.irm_param_groups_id,
            param_group_member_id: paramMemberId,
            param_group_member_option_id: null,
            name: paramMember?.name,
            value: valueToUse,
            history_date: currentDate,
            remarks: null,
            private_note: editingRecord.private_note || Math.floor(Date.now() / 1000).toString()
          });
        }
      });

      if (updates.length === 0 && creates.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Cannot Update',
          text: 'No matching history parameters found to save against.',
          confirmButtonColor: '#00798E'
        });
        return;
      }

      await Promise.all(updates.map(update => api.opd.updateContactHistory(update)));
      if (creates.length > 0) {
        await api.opd.createContactHistory(creates);
      }

      // Re-fetch so newly created records' ids get picked up for future edits
      const refreshedEntries = await fetchExistingMenstrualHistory(contactId);
      setEntries(refreshedEntries);
      setEditingRecord(null);

      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Menstrual history updated successfully',
        confirmButtonColor: '#00798E',
        timer: 2000,
        showConfirmButton: true
      });
    } catch (error) {
      console.error('Error updating menstrual history:', error);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Failed to update menstrual history'
      });
    }
  };

  const clearCurrentForm = () => {
    setCurrentEntry({
      lmp: '',
      menarche: '',
      cycleLength: '',
      duration: '',
      flow: '',
      pain: '',
      other: ''
    });
  };

  const formatSummary = (summaryArray) => {
    if (!summaryArray || summaryArray.length === 0) return 'None';
    return summaryArray.map(item => 
      item.charAt(0).toUpperCase() + item.slice(1)
    ).join(', ');
  };

  const selectBoxHeight = 40; // Adjust as needed

  return (
    <MDBox>
      {/* Existing Menstrual History Table */}
      {entries.length > 0 && (
        <MDBox mb={4}>
          <Typography variant="h6" gutterBottom sx={{ color: '#00798E', fontWeight: 'bold' }}>
            Existing Menstrual History Records
          </Typography>
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>LMP</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Menarche</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Cycle Length</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Flow</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Pain</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Other Notes</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#00798E', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
                {entries.map((entry, index) => {
                  const isEditing = editingRecord?.id === entry.id;

                  return (
                  <TableRow
                    key={entry.id || index}
                    sx={{
                      backgroundColor: isEditing ? 'rgba(0, 121, 142, 0.06)' : 'inherit',
                      borderLeft: isEditing ? '3px solid #00798E' : '3px solid transparent',
                      '&:nth-of-type(odd)': { backgroundColor: isEditing ? 'rgba(0, 121, 142, 0.06)' : '#fafafa' },
                      '&:hover': { backgroundColor: isEditing ? 'rgba(0, 121, 142, 0.09)' : '#e3f2fd' },
                      '& > .MuiTableCell-root': { verticalAlign: 'top', pt: isEditing ? 1.5 : 1, pb: isEditing ? 1.5 : 1 },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <TableCell>
                      {isEditing ? (
                        <MDDatePicker
                          value={editingRecord.lmp || ''}
                          onChange={(date) => setEditingRecord({ ...editingRecord, lmp: date })}
                          textFieldProps={{ fullWidth: true, size: 'small', required: true }}
                        />
                      ) : (
                        entry.lmp ? formatDateDDMMYYYY(entry.lmp) : 'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          value={editingRecord.menarche || ''}
                          onChange={(e) => setEditingRecord({ ...editingRecord, menarche: e.target.value.toUpperCase() })}
                          fullWidth
                          size="small"
                          inputProps={{ autoComplete: 'off' }}
                        />
                      ) : (
                        entry.menarche || 'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        cycleLengthOptions.length > 0 ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={editingRecord.cycleLength || ''}
                              onChange={(e) => setEditingRecord({ ...editingRecord, cycleLength: e.target.value })}
                              sx={{ height: selectBoxHeight }}
                            >
                              {cycleLengthOptions.map((option) => (
                                <MenuItem key={option.id} value={option.option_value}>
                                  {option.option_value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <TextField
                            value={editingRecord.cycleLength || ''}
                            onChange={(e) => setEditingRecord({ ...editingRecord, cycleLength: e.target.value.toUpperCase() })}
                            fullWidth
                            size="small"
                            inputProps={{ autoComplete: 'off' }}
                          />
                        )
                      ) : (
                        entry.cycleLength || 'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          value={editingRecord.duration || ''}
                          onChange={(e) => setEditingRecord({ ...editingRecord, duration: e.target.value.toUpperCase() })}
                          fullWidth
                          size="small"
                          inputProps={{ autoComplete: 'off' }}
                        />
                      ) : (
                        entry.duration ? `${entry.duration} days` : 'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        flowOptions.length > 0 ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={editingRecord.flow || ''}
                              onChange={(e) => setEditingRecord({ ...editingRecord, flow: e.target.value })}
                              sx={{ height: selectBoxHeight }}
                            >
                              {flowOptions.map((option) => (
                                <MenuItem key={option.id} value={option.option_value}>
                                  {option.option_value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <TextField
                            value={editingRecord.flow || ''}
                            onChange={(e) => setEditingRecord({ ...editingRecord, flow: e.target.value.toUpperCase() })}
                            fullWidth
                            size="small"
                            inputProps={{ autoComplete: 'off' }}
                          />
                        )
                      ) : (
                        entry.flow || 'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        painOptions.length > 0 ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={editingRecord.pain || ''}
                              onChange={(e) => setEditingRecord({ ...editingRecord, pain: e.target.value })}
                              sx={{ height: selectBoxHeight }}
                            >
                              {painOptions.map((option) => (
                                <MenuItem key={option.id} value={option.option_value}>
                                  {option.option_value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <TextField
                            value={editingRecord.pain || ''}
                            onChange={(e) => setEditingRecord({ ...editingRecord, pain: e.target.value.toUpperCase() })}
                            fullWidth
                            size="small"
                            inputProps={{ autoComplete: 'off' }}
                          />
                        )
                      ) : (
                        entry.pain || 'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <RichTextEditor
                          value={editingRecord.other || ''}
                          onChange={(value) => setEditingRecord({ ...editingRecord, other: value })}
                        />
                      ) : entry.other ? (
                        <Box
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            '& p, & ul, & ol': {
                              margin: 0,
                              padding: 0
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: entry.other }}
                        />
                      ) : (
                        <Typography variant="body2">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <>
                          <IconButton
                            aria-label="save"
                            color="success"
                            size="small"
                            disabled={loading}
                            onClick={handleSaveEditedMenstrualHistory}
                            title="Save changes"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="cancel"
                            color="default"
                            size="small"
                            disabled={loading}
                            onClick={() => setEditingRecord(null)}
                            title="Cancel editing"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            aria-label="edit"
                            size="small"
                            disabled={loading}
                            onClick={() => setEditingRecord({ ...entry })}
                            title="Edit this menstrual history record"
                            sx={{
                              color: '#00798E',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 121, 142, 0.08)',
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {entry.contact_history_ids && entry.contact_history_ids.length > 0 && (
                            <IconButton
                              onClick={() => handleDeleteMenstrualHistory(entry, index)}
                              disabled={loading}
                              size="small"
                              sx={{
                                color: '#d32f2f',
                                '&:hover': {
                                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                }
                              }}
                              title="Delete this menstrual history record"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      )}

      {/* Add New Menstrual History Form */}
      <MDBox>
        <MDBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#00798E', fontWeight: 'bold', mb: 0 }}>
            Add New Menstrual History Record
          </Typography>
          <LoadDraftButton
            hasDraftData={menstrualDraft.hasDraftData}
            onLoadDraft={handleLoadMenstrualDraft}
            onClearDraft={async () => await menstrualDraft.clearDraft()}
            timestamp={menstrualDraft.getFormattedTimestamp()}
          />
        </MDBox>
        <Paper elevation={2} sx={{ p: 3 }}>
          <form onSubmit={handleAddEntry}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <MDDatePicker
                  label="LMP (Last Menstrual Period)"
                  value={currentEntry.lmp || ''}
                  onChange={(date) => handleInputChange('lmp', date)}
                  textFieldProps={{
                    fullWidth: true,
                    size: 'small',
                    required: true,
                    disabled: loading,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#00798E',
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Menarche (Age of first period)"
                  value={currentEntry.menarche}
                  onChange={(e) => handleInputChange('menarche', e.target.value.toUpperCase())}
                  fullWidth
                  size="small"
                  disabled={loading}
                  inputProps={{ autoComplete: 'off' }}
                  sx={{ 
                    '& .MuiInputBase-input': { 
                      textTransform: 'uppercase' 
                    } 
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {cycleLengthOptions.length > 0 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Cycle Length (days)</InputLabel>
                    <Select
                      value={currentEntry.cycleLength}
                      label="Cycle Length (days)"
                      onChange={(e) => handleInputChange('cycleLength', e.target.value)}
                      disabled={loading}
                      sx={{ height: selectBoxHeight }}
                    >
                      {cycleLengthOptions.map((option) => (
                        <MenuItem key={option.id} value={option.option_value}>
                          {option.option_value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="Cycle Length (days)"
                    value={currentEntry.cycleLength}
                    onChange={(e) => handleInputChange('cycleLength', e.target.value.toUpperCase())}
                    fullWidth
                    size="small"
                    disabled={loading}
                    inputProps={{ autoComplete: 'off' }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        textTransform: 'uppercase' 
                      } 
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Duration (days)"
                  value={currentEntry.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value.toUpperCase())}
                  fullWidth
                  size="small"
                  disabled={loading}
                  inputProps={{ autoComplete: 'off' }}
                  sx={{ 
                    '& .MuiInputBase-input': { 
                      textTransform: 'uppercase' 
                    } 
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {flowOptions.length > 0 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Flow</InputLabel>
                    <Select
                      value={currentEntry.flow}
                      label="Flow"
                      onChange={(e) => handleInputChange('flow', e.target.value)}
                      disabled={loading}
                      sx={{ height: selectBoxHeight }}
                    >
                      {flowOptions.map((option) => (
                        <MenuItem key={option.id} value={option.option_value}>
                          {option.option_value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="Flow"
                    value={currentEntry.flow}
                    onChange={(e) => handleInputChange('flow', e.target.value.toUpperCase())}
                    fullWidth
                    size="small"
                    disabled={loading}
                    inputProps={{ autoComplete: 'off' }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        textTransform: 'uppercase' 
                      } 
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {painOptions.length > 0 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Pain</InputLabel>
                    <Select
                      value={currentEntry.pain}
                      label="Pain"
                      onChange={(e) => handleInputChange('pain', e.target.value)}
                      disabled={loading}
                      sx={{ height: selectBoxHeight }}
                    >
                      {painOptions.map((option) => (
                        <MenuItem key={option.id} value={option.option_value}>
                          {option.option_value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="Pain"
                    value={currentEntry.pain}
                    onChange={(e) => handleInputChange('pain', e.target.value.toUpperCase())}
                    fullWidth
                    size="small"
                    disabled={loading}
                    inputProps={{ autoComplete: 'off' }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        textTransform: 'uppercase' 
                      } 
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <RichTextEditor
                  value={currentEntry.other}
                  onChange={(value) => handleInputChange('other', value)}
                  label="Other Notes"
                  height="150px"
                  placeholder="Enter other notes..."
                />
              </Grid>
              <Grid item xs={12}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2} mt={2}>
                  <MDBox display="flex" gap={1}>
                    <IconButton
                      onClick={handleAddEntry}
                      disabled={loading}
                      sx={{
                        backgroundColor: '#00798E',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#006470',
                        },
                        '&:disabled': {
                          backgroundColor: '#cccccc',
                          color: '#666666',
                        },
                        width: 40,
                        height: 40,
                      }}
                      title="Quick add current entry"
                    >
                      <AddIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <button
                      type="button"
                      onClick={clearCurrentForm}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        background: 'transparent',
                        color: '#00798E',
                        border: '1px solid #00798E',
                        borderRadius: 4,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        opacity: loading ? 0.6 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: 16 }} />
                      Clear
                    </button>
                  </MDBox>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      padding: '12px 32px',
                      background: '#00798E',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      opacity: loading ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 4px rgba(0, 121, 142, 0.2)',
                    }}
                  >
                    Save Menstrual History
                  </button>
                </MDBox>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </MDBox>
    </MDBox>
  );
}

MenstrualHistoryForm.propTypes = {
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
  previousData: PropTypes.array,
  onDelete: PropTypes.func,
  menstrualParams: PropTypes.object,
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  patientGender: PropTypes.string,
};

export default MenstrualHistoryForm;
